import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Actor, MeResponse, Permission } from '@hrms/shared';
import { ME_QUERY_KEY, tokens, tsr } from '../lib/api.js';
import { actorFromMe } from './actorFromMe.js';

export type Session =
  | { status: 'loading'; me?: undefined; actor?: undefined; permissions: ReadonlySet<Permission> }
  | { status: 'anonymous'; me?: undefined; actor?: undefined; permissions: ReadonlySet<Permission> }
  | {
      status: 'error';
      me?: undefined;
      actor?: undefined;
      permissions: ReadonlySet<Permission>;
      retry: () => void;
    }
  | { status: 'authenticated'; me: MeResponse; actor: Actor; permissions: ReadonlySet<Permission> };

const NO_PERMISSIONS: ReadonlySet<Permission> = new Set();

const SessionContext = createContext<Session | null>(null);

/**
 * Who is signed in, for the whole tree.
 *
 * The source of truth for "is there a session" is the token store, observed through
 * useSyncExternalStore — not the `me` query. When the fetcher gives up on a refresh it calls
 * `tokens.clear()`, the store notifies, `hasSession` flips, and this provider reports `anonymous`
 * without anyone having to catch a 401 in a component. That is also why `me` is `enabled` only
 * while a refresh token exists: with none there is nothing to resume, and the request would be a
 * guaranteed 401 round trip on every visit to /login.
 *
 * `error` is distinct from `anonymous` on purpose. The API being down, or a 5xx, must not bounce a
 * perfectly valid session to the login screen — where login would fail for the same reason.
 * A 401 never reaches this branch: the fetcher has already cleared the store by then.
 *
 * `permissions` is the set the API computed server-side and is for gating NAVIGATION and disabling
 * controls only. It is not an authorisation decision — see meResponseSchema in @hrms/shared.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const hasSession = useSyncExternalStore(tokens.subscribe, tokens.hasSession, () => false);

  const me = tsr.auth.me.useQuery({
    queryKey: ME_QUERY_KEY,
    enabled: hasSession,
    staleTime: Infinity,
    retry: false,
  });

  // Tracks whether THIS provider has ever observed a session, so the effect below can tell an
  // actual sign-out (true -> false) apart from a fresh, never-authenticated mount, where
  // `hasSession` is false from the very first render — every public page (the career site, an
  // offer-acceptance link, a "pick a slot" link) mounts in exactly that state. Without this
  // distinction the effect ran on EVERY such mount, and `removeQueries()` cancels every in-flight
  // query app-wide by aborting its fetch's AbortSignal (TanStack Query's own cancellation
  // mechanism) — including the query the very page rendering this provider's children had just
  // started, a heartbeat earlier, in its own effect. That raced this effect against the page's
  // own fetch and could abort it before it ever reached the network, which is what a public link
  // landing on a permanently blank page actually was: not a crash, a torn-down request.
  const everHadSession = useRef(hasSession);
  useEffect(() => {
    // Signing out (here or in another tab) must not leave the previous user's data in the cache
    // for the next person who signs in on this machine — but only on that actual transition.
    if (everHadSession.current && !hasSession) queryClient.removeQueries();
    everHadSession.current = hasSession;
  }, [hasSession, queryClient]);

  const value = useMemo<Session>(() => {
    if (!hasSession) return { status: 'anonymous', permissions: NO_PERMISSIONS };
    if (me.isPending) return { status: 'loading', permissions: NO_PERMISSIONS };
    if (me.isError)
      return { status: 'error', permissions: NO_PERMISSIONS, retry: () => void me.refetch() };

    const body = me.data.body;
    const { actor, permissions } = actorFromMe(body);
    return { status: 'authenticated', me: body, actor, permissions };
  }, [hasSession, me]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession() called outside <SessionProvider>');
  return session;
}

/** For components that only render inside RequireAuth and would rather not narrow every time. */
export function useAuthenticatedSession(): Extract<Session, { status: 'authenticated' }> {
  const session = useSession();
  if (session.status !== 'authenticated') {
    throw new Error('useAuthenticatedSession() rendered outside an authenticated route');
  }
  return session;
}
